import type { ApiErrorBody } from '@/api/types'
import { ApiError } from '@/api/client'

/**
 * The customer portal's own way of talking to the server.
 *
 * <p>Deliberately separate from the staff client. A customer's token is not a
 * staff token and is kept under its own key, so signing in to the portal on a
 * shared machine cannot disturb a member of staff's session, and neither token
 * can be sent to the other's endpoints by accident.</p>
 */
const TOKEN_KEY = 'catering.portalToken'
const NAME_KEY = 'catering.portalName'

export const portalSession = {
  token: () => localStorage.getItem(TOKEN_KEY),
  name: () => localStorage.getItem(NAME_KEY),
  signedIn: () => Boolean(localStorage.getItem(TOKEN_KEY)),
  save(token: string, name: string) {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(NAME_KEY, name)
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(NAME_KEY)
  },
}

async function send<T>(path: string, method: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const token = portalSession.token()
  if (token) headers.Authorization = `Bearer ${token}`

  const response = await fetch(`/api/v1/portal${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (response.status === 401) {
    // The token has expired or been withdrawn. Sending them back to the sign-in
    // page is kinder than an error they can do nothing about.
    portalSession.clear()
    window.dispatchEvent(new CustomEvent('portal:signed-out'))
  }
  if (!response.ok) {
    let parsed: Partial<ApiErrorBody> = {}
    try {
      parsed = (await response.json()) as ApiErrorBody
    } catch {
      parsed = { message: response.statusText }
    }
    throw new ApiError(response.status, parsed)
  }
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

/**
 * Opens one of the customer's own documents.
 *
 * <p>Fetched rather than linked, because the portal token travels in a header
 * and a plain link cannot carry one. The PDF is turned into a blob and opened
 * in a new tab, which is what somebody wants when they are about to read a
 * quotation and then accept it.</p>
 */
export async function openPortalPdf(path: string): Promise<void> {
  const token = portalSession.token()
  const response = await fetch(`/api/v1/portal${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!response.ok) {
    let parsed: Partial<ApiErrorBody> = {}
    try {
      parsed = (await response.json()) as ApiErrorBody
    } catch {
      parsed = { message: response.statusText }
    }
    throw new ApiError(response.status, parsed)
  }
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener')
  // Freed once the new tab has taken it; revoking at once would blank the page.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

export const portalApi = {
  get: <T>(path: string) => send<T>(path, 'GET'),
  post: <T>(path: string, body?: unknown) => send<T>(path, 'POST', body),
}

// ------------------------------------------------------------------- shapes

export interface PortalSummary {
  customerName: string
  openEnquiries: number
  quotationsToDecide: number
  upcomingEvents: number
  outstanding: number
  upcoming: {
    reference: string
    eventType: string
    eventDate: string
    guestCount: number
    status: string
  }[]
}

export interface PortalEnquiry {
  id: string
  enquiryNumber: string
  eventType: string
  eventDate: string
  guestCount: number
  status: string
  raisedByYou: boolean
  createdAt: string
}

export interface PortalQuotation {
  id: string
  quotationNumber: string
  eventType: string
  eventDate: string
  guestCount: number
  totalAmount: number
  status: string
  validUntil?: string
  awaitingYourDecision: boolean
}

export interface PortalFeedback {
  orderNumber: string
  eventType: string
  eventDate: string
  submittedAt: string
  overallRating: number
  salesExperience: number | null
  foodQuality: number | null
  taste: number | null
  presentation: number | null
  service: number | null
  staffBehaviour: number | null
  timeliness: number | null
  cleanliness: number | null
  valueForMoney: number | null
  npsScore: number | null
  comments: string | null
}

export interface PortalOrder {
  id: string
  orderNumber: string
  eventType: string
  eventDate: string
  guestCount: number
  totalAmount: number
  paidAmount: number
  balanceAmount: number
  status: string
}

export interface PortalInvoice {
  id: string
  invoiceNumber: string
  invoiceDate: string
  dueDate?: string
  totalAmount: number
  balanceAmount: number
  status: string
}

/**
 * A line of business the caterer sells.
 *
 * `spaceType` decides whether there is a sitting of ours to choose (HALL for a
 * banquet, DINING for a restaurant party, absent for a job we come out to), and
 * `needsDeliveryAddress` whether we have to ask where.
 */
export interface PortalService {
  code: string
  name: string
  spaceType: string | null
  needsDeliveryAddress: boolean
}

export interface PortalSlot {
  slotId: string
  slotCode: string
  name: string
  startTime: string
  endTime: string
  rentalCharge: number | null
  available: boolean
}

export interface PortalHallDay {
  hallId: string
  hallName: string
  outletId: string
  outletName: string | null
  seatingCapacity: number
  rentalCharge: number | null
  anyAvailable: boolean
  slots: PortalSlot[]
}

export interface PortalChoiceItem {
  menuItemId: string
  variantId: string | null
  name: string
  dietType: string | null
  mandatory: boolean
}

export interface PortalPackageChoice {
  packageItemId: string
  itemNumber: number
  label: string
  categoryName: string | null
  minSelections: number
  maxSelections: number
  items: PortalChoiceItem[]
}

export interface PortalPackage {
  packageId: string
  packageVersionId: string
  code: string
  name: string
  serviceType: string
  sessionType: string
  pricePerPerson: number
  minimumGuaranteeGuests: number
  taxRatePercent: number
  choices: PortalPackageChoice[]
}

/** What the customer has put together, sent for pricing and then for quoting. */
export interface PortalPlan {
  serviceType: string
  eventType: string
  eventDate: string
  guestCount: number
  hallId?: string
  hallSlotId?: string
  deliveryAddress?: string
  packageId?: string
  packageVersionId: string
  menuSelections: { menuItemId: string; variantId?: string; packageItemId?: string }[]
  notes?: string
}

export interface PortalEstimate {
  guestCount: number
  billedGuestCount: number
  pricePerPerson: number
  packageAmount: number
  extraItemAmount: number
  hallRentalAmount: number
  subtotal: number
  taxRatePercent: number
  taxAmount: number
  total: number
  valid: boolean
  issues: string[]
  lines: { description: string; quantity: number; unitPrice: number; amount: number }[]
}

export interface PortalPayment {
  id: string
  receiptNumber: string
  paymentDate: string
  amount: number
  mode: string
  reference?: string
}

/** A mobile number in any usual form - +91, spaces, dashes - as long as it has ten digits. */
export const hasMobile = (value: string) => value.replace(/\D/g, '').length >= 10
